<template>
  <v-dialog v-model="dialog" max-width="650px">
    <template v-slot:activator="{ on }">
      <v-btn v-on="on" icon>
        <v-icon color="white"> mdi-pencil </v-icon>
      </v-btn>
    </template>

    <v-card :loading="loading">
      <v-toolbar class="white--text primary mb-2">
        <v-spacer />
        <v-toolbar-title> Editar Informações do Fundo </v-toolbar-title>
        <v-spacer />
        <template v-slot:extension>
          <v-tabs v-model="activeTab" centered grow>
            <v-tab
              v-for="tab in tabs"
              :key="tab.value"
              :href="tab.href"
              :class="activeTab === tab.value ? 'active-tab' : 'inactive-tab'"
            >
              {{ tab.text }}
            </v-tab>
          </v-tabs>
        </template>
      </v-toolbar>
      <v-card-text>
        <v-tabs-items v-model="activeTab">
          <v-tab-item
            v-for="(tab, index) in tabs"
            :key="index + reRenderTrigger"
            :value="tab.value"
            eager
          >
            <component
              :is="tab.componentName"
              :fund="formFields"
              :value="tab.value"
              @change="handleFormChange"
            />
          </v-tab-item>
        </v-tabs-items>
        <v-container>
          <v-row dense justify="end">
            <v-col cols="9" sm="auto">
              <v-btn text block color="error" :disabled="loading" @click="cancel">
                Descartar
              </v-btn>
            </v-col>
            <v-col cols="9" sm="auto">
              <v-btn
                block
                color="primary"
                type="submit"
                :disabled="!isFormValid || loading"
                :loading="loading"
                @click="update"
              >
                Atualizar
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/services/api';
import fillSchema from '@/utils/fill-schema';
import { parseNullableNumber } from '@/utils/parse-utils';
import FundIntegrationFormTab from './FundIntegrationFormTab.vue';
import FundManagementFormTab from './FundManagementFormTab.vue';
import FundReportFormTab from './FundReportFormTab.vue';

const formSchema = {
  shortName: null,
  manager: { _id: null },
  administrator: { _id: null },

  custodyBank: null,
  custodyAgency: null,
  custodyAccount: null,

  purchase: {
    daysToConvertion: null,
    daysToConvertionType: null,
    daysToSettlement: null,
    daysToSettlementType: null,
  },

  withdrawal: {
    daysToConvertion: null,
    daysToConvertionType: null,
    daysToSettlement: null,
    daysToSettlementType: null,
  },

  externalIds: [],

  reportName: null,
  reportType: null,
};

export default {
  name: 'FundEditForm',

  props: {
    fund: {
      type: Object,
      default: () => ({}),
    },
  },

  components: {
    FundManagementFormTab,
    FundIntegrationFormTab,
    FundReportFormTab,
  },

  watch: {
    fund: {
      immediate: true,
      deep: true,
      handler(updatedFundData) {
        this.formFields = fillSchema(formSchema, updatedFundData);
      },
    },
  },

  data: (vm) => ({
    formFields: fillSchema(formSchema, vm.fund),

    loading: false,
    dialog: false,
    reRenderTrigger: 0,
    activeTab: 'management',

    tabs: [
      {
        value: 'management',
        href: '#management',
        text: 'Operacional',
        componentName: 'FundManagementFormTab',
        isFormValid: false,
      },
      {
        value: 'integration',
        href: '#integration',
        text: 'Integrações',
        componentName: 'FundIntegrationFormTab',
        isFormValid: false,
      },
      {
        value: 'report',
        href: '#report',
        text: 'Relatórios',
        componentName: 'FundReportFormTab',
        isFormValid: false,
      },
    ],
  }),

  computed: {
    isFormValid(vm) {
      return vm.tabs.every((tab) => tab.isFormValid);
    },
  },

  methods: {
    handleFormChange(updatedTabValue, formFields, isFormValid) {
      this.formFields = { ...this.formFields, ...formFields };

      const updatedTab = this.tabs.find((tab) => tab.value === updatedTabValue);

      if (updatedTab) {
        updatedTab.isFormValid = isFormValid;
      }
    },

    resetForm() {
      this.formFields = fillSchema(formSchema, this.fund);
      this.activeTab = 'management';
      this.reRenderTrigger += 1; // Force tabs re-render on form discard
    },

    cancel() {
      this.dialog = false;
      // Delay form reset to avoid visual bugs during dialog close
      setTimeout(this.resetForm, 500);
    },

    async update() {
      this.loading = true;

      console.log('b', this.formFields);

      const parsedFormFields = {
        ...this.formFields,
        manager: this.formFields?.manager?._id,
        administrator: this.formFields?.administrator?._id,

        purchase: {
          ...this.formFields?.purchase,
          daysToConvertion: parseNullableNumber(this.formFields?.purchase?.daysToConvertion),
          daysToSettlement: parseNullableNumber(this.formFields?.purchase?.daysToSettlement),
        },
        withdrawal: {
          ...this.formFields?.withdrawal,
          daysToConvertion: parseNullableNumber(this.formFields?.withdrawal?.daysToConvertion),
          daysToSettlement: parseNullableNumber(this.formFields?.withdrawal?.daysToSettlement),
        },
      };

      console.log('a', parsedFormFields);

      try {
        const { data } = await api.funds.updateById(this.fund._id, parsedFormFields);
        this.$emit('update', data);
        this.dialog = false;
        this.activeTab = 'management';
      } catch (error) {
        this.$store.dispatch('notification', {
          text: error.response?.data.message ?? error.message,
          color: 'error',
          status: true,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.active-tab {
  color: white;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.05));
}
.inactive-tab {
  color: white !important;
  opacity: 0.3;
}
</style>
